import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  TrendingUp, 
  Lightbulb,
  BarChart3,
  Target,
  RefreshCw,
  Eye,
  ThumbsUp,
  ThumbsDown,
  DollarSign,
  Users,
  MapPin,
  Briefcase
} from 'lucide-react';
import { validateJobDescription, ValidationResult } from '@/services/jobDescriptionValidation';
import { useToast } from '../../../hooks/useToast';

interface JobDescriptionValidationProps {
  jobData: {
    jobTitle: string;
    company: string;
    location?: string;
    industry?: string;
    experience?: string;
    experienceLevel?: string;
    skills: string;
    responsibilities: string;
    qualifications?: string;
    benefits?: string;
    salary?: string;
    content?: string;
  };
  onValidationComplete?: (result: ValidationResult) => void;
  className?: string;
}

export function JobDescriptionValidation({ 
  jobData, 
  onValidationComplete,
  className = ""
}: JobDescriptionValidationProps) {
  const { addToast } = useToast();
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const handleFullValidation = async () => {
    if (!jobData.jobTitle || !jobData.skills || !jobData.responsibilities) {
      addToast('Please complete the essential job details before validation', 'error');
      return;
    }

    setIsValidating(true);
    try {
      addToast('🤖 AI is analyzing your job description...', 'info');
      
      const result = await validateJobDescription(jobData);
      setValidationResult(result);
      
      if (onValidationComplete) {
        onValidationComplete(result);
      }

      if (result.overall_score >= 80) {
        addToast('✅ Excellent job description! Well-structured and market-aligned.', 'success');
      } else if (result.overall_score >= 60) {
        addToast('⚠️ Good foundation with some areas for improvement.', 'info');
      } else {
        addToast('⚠️ Several issues found. Review recommendations below.', 'info');
      }
    } catch (error) {
      console.error('Validation error:', error);
      addToast('Failed to validate job description. Please try again.', 'error');
    } finally {
      setIsValidating(false);
    }
  };

  // Reset validation results when core job details change (not content)
  // This ensures users don't see stale validation results for old job details
  // but preserves validation when only content is updated (e.g., via audience targeting)
  React.useEffect(() => {
    if (validationResult) {
      setValidationResult(null);
    }
  }, [jobData.jobTitle, jobData.skills, jobData.responsibilities, jobData.experience]);

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'high':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'medium':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'low':
        return <Eye className="h-4 w-4 text-blue-500" />;
      default:
        return <Eye className="h-4 w-4 text-gray-500" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'medium':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'low':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'salary':
        return <DollarSign className="h-4 w-4" />;
      case 'experience':
        return <Target className="h-4 w-4" />;
      case 'skills':
        return <BarChart3 className="h-4 w-4" />;
      case 'responsibilities':
        return <Briefcase className="h-4 w-4" />;
      case 'qualifications':
        return <Users className="h-4 w-4" />;
      case 'benefits':
        return <ThumbsUp className="h-4 w-4" />;
      default:
        return <Eye className="h-4 w-4" />;
    }
  };

  const getMarketInsightIcon = (type: string) => {
    switch (type) {
      case 'salary_range':
        return <DollarSign className="h-4 w-4 text-green-600" />;
      case 'skill_demand':
        return <TrendingUp className="h-4 w-4 text-blue-600" />;
      case 'experience_level':
        return <Target className="h-4 w-4 text-purple-600" />;
      case 'location_factor':
        return <MapPin className="h-4 w-4 text-orange-600" />;
      case 'industry_trend':
        return <BarChart3 className="h-4 w-4 text-indigo-600" />;
      default:
        return <Lightbulb className="h-4 w-4 text-gray-600" />;
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Validation Trigger */}
      {!validationResult && (
        <div className="text-center">
          <Button
            onClick={handleFullValidation}
            disabled={isValidating}
            className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-3"
            size="lg"
          >
            {isValidating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                Analyzing Job Description...
              </>
            ) : (
              <>
                <Target className="h-4 w-4 mr-2" />
                Validate Job Description
              </>
            )}
          </Button>
        </div>
      )}

      {/* Re-validation option when results exist */}
      {validationResult && (
        <div className="text-center mb-6">
          <Button
            onClick={handleFullValidation}
            disabled={isValidating}
            variant="outline"
            size="sm"
            className="text-slate-600 hover:text-slate-900 border-slate-300 hover:bg-slate-50"
          >
            {isValidating ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-slate-600 border-t-transparent mr-2" />
                Re-analyzing...
              </>
            ) : (
              <>
                <Target className="h-3 w-3 mr-2" />
                Re-validate with Latest Content
              </>
            )}
          </Button>
        </div>
      )}

      {/* Full Validation Results */}
      {validationResult && (
        <div className="space-y-6">
          {/* Overall Score */}
          <Card className="bg-white border border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                  validationResult.overall_score >= 80 ? 'bg-green-100' :
                  validationResult.overall_score >= 60 ? 'bg-yellow-100' : 'bg-red-100'
                }`}>
                  {validationResult.is_realistic ? (
                    <ThumbsUp className="h-5 w-5 text-green-600" />
                  ) : (
                    <ThumbsDown className="h-5 w-5 text-red-600" />
                  )}
                </div>
                <div>
                  <span className="text-lg font-bold">
                    Overall Score: {validationResult.overall_score}/100
                  </span>
                  <p className="text-sm text-slate-600 font-normal">
                    {validationResult.is_realistic ? 'Realistic expectations' : 'Some expectations may be unrealistic'}
                  </p>
                </div>
              </CardTitle>
            </CardHeader>
                         <CardContent className="space-y-4">
               <div className="w-full bg-gray-200 rounded-full h-2">
                 <div 
                   className={`h-2 rounded-full transition-all duration-500 ${
                     validationResult.overall_score >= 80 ? 'bg-green-500' :
                     validationResult.overall_score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                   }`}
                   style={{ width: `${validationResult.overall_score}%` }}
                 />
               </div>
               
               <Alert>
                <Lightbulb className="h-4 w-4" />
                <AlertDescription>
                  <strong>Summary:</strong> {validationResult.summary}
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Issues & Recommendations */}
          {validationResult.issues.length > 0 && (
            <Card className="bg-white border border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Issues Found ({validationResult.issues.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {validationResult.issues.map((issue, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border ${getSeverityColor(issue.severity)}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex items-center gap-2 mt-0.5">
                        {getSeverityIcon(issue.severity)}
                        {getCategoryIcon(issue.category)}
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs capitalize">
                            {issue.category.replace('_', ' ')}
                          </Badge>
                          <Badge 
                            variant={issue.severity === 'high' ? 'destructive' : 
                                   issue.severity === 'medium' ? 'default' : 'secondary'}
                            className="text-xs capitalize"
                          >
                            {issue.severity}
                          </Badge>
                        </div>
                        <p className="font-medium">{issue.issue}</p>
                        <p className="text-sm opacity-90">
                          <strong>Suggestion:</strong> {issue.suggestion}
                        </p>
                        <p className="text-xs opacity-75">
                          <strong>Impact:</strong> {issue.impact}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Recommendations */}
          {validationResult.recommendations.length > 0 && (
            <Card className="bg-green-50 border border-green-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-800">
                  <Lightbulb className="h-5 w-5" />
                  Recommendations ({validationResult.recommendations.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {validationResult.recommendations.map((recommendation, index) => (
                    <li key={index} className="flex items-start gap-2 text-green-800">
                      <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-600" />
                      <span className="text-sm">{recommendation}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Market Insights */}
          {validationResult.market_insights.length > 0 && (
            <Card className="bg-blue-50 border border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-800">
                  <TrendingUp className="h-5 w-5" />
                  Market Insights ({validationResult.market_insights.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {validationResult.market_insights.map((insight, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-blue-100">
                    {getMarketInsightIcon(insight.type)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs capitalize text-blue-700">
                          {insight.type.replace('_', ' ')}
                        </Badge>
                        <span className="text-xs text-blue-600">
                          {insight.confidence}% confidence
                        </span>
                      </div>
                      <p className="text-sm text-blue-800">{insight.insight}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Re-validate Button */}
          <div className="flex justify-center">
            <Button
              onClick={handleFullValidation}
              variant="outline"
              disabled={isValidating}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isValidating ? 'animate-spin' : ''}`} />
              Re-validate Job Description
            </Button>
          </div>
        </div>
      )}
    </div>
  );
} 